import { useContext, useEffect, useRef } from "react";

import { queryClient } from "utils/queryClient";
import { setRealtimeConnectionId } from "utils/realtimeConnection";

import { AuthTokenContext } from "components/AuthProvider/AuthProviderContext";

import { getRealtimeConnectionId } from "../../utils/realtimeConnection";

type RealtimeEvent =
  | { type: "realtime.connection.ready"; payload: { connectionId: string } }
  | {
      type: "session.status.changed";
      payload: { sessionId: number; oldStatusId?: number; newStatusId: number; connectionId: string };
    };

const MAX_RECONNECT_DELAY = 30_000;

function buildWsUrl(): string {
  const base = (import.meta.env.VITE_API_URL as string).replace(/^https/, "wss").replace(/^http/, "ws").replace(/\/?$/, "/");
  return `${base}ws/notifications`;
}

function handleMessage(event: RealtimeEvent) {
  if (event.type === "realtime.connection.ready") {
    setRealtimeConnectionId(event.payload.connectionId);
    return;
  }

  if (event.type === "session.status.changed" && event.payload.connectionId !== getRealtimeConnectionId()) {
    const { sessionId } = event.payload;
    queryClient.invalidateQueries({ queryKey: ["session", String(sessionId)] });
    queryClient.invalidateQueries({ queryKey: ["sessions"] });
  }
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const token = useContext(AuthTokenContext);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;

    if (!token) return;

    function connect() {
      if (unmountedRef.current) return;

      const ws = new WebSocket(buildWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "auth", token: token! }));
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const message = JSON.parse(event.data) as RealtimeEvent;
          handleMessage(message);
        } catch {
          // non-JSON frame, ignore
        }
      };

      ws.onclose = () => {
        setRealtimeConnectionId(null);
        if (unmountedRef.current) return;
        const delay = Math.min(1_000 * 2 ** reconnectAttemptsRef.current, MAX_RECONNECT_DELAY);
        reconnectAttemptsRef.current++;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      unmountedRef.current = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      wsRef.current?.close();
    };
  }, [token]);

  return <>{children}</>;
}
